import React, { useState, useEffect, useCallback } from 'react';
import { getSoundManager, SFX_KEYS } from '../game/systems/SoundManager';

// ============ PERSISTENCE ============

const RESEARCHED_ISLANDS_KEY = 'grandline_researched_islands';

const getResearchedIslands = (): Record<string, IslandResearchData> => {
  try {
    const stored = localStorage.getItem(RESEARCHED_ISLANDS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveResearchedIsland = (data: IslandResearchData): void => {
  try {
    const existing = getResearchedIslands();
    existing[data.id] = data;
    localStorage.setItem(RESEARCHED_ISLANDS_KEY, JSON.stringify(existing));
  } catch {
    console.error('Failed to save researched island');
  }
};

const getResearchedIsland = (islandId: string): IslandResearchData | null => {
  const islands = getResearchedIslands();
  return islands[islandId] || null;
};

const clearResearchedIsland = (islandId: string): void => {
  try {
    const existing = getResearchedIslands();
    delete existing[islandId];
    localStorage.setItem(RESEARCHED_ISLANDS_KEY, JSON.stringify(existing));
  } catch {
    console.error('Failed to clear researched island');
  }
};

const clearAllResearchedIslands = (): void => {
  try {
    localStorage.removeItem(RESEARCHED_ISLANDS_KEY);
  } catch {
    console.error('Failed to clear all researched islands');
  }
};

// Export cache functions for external use (admin panel, socket events)
export { clearResearchedIsland, clearAllResearchedIslands };

// ============ TYPES ============

interface IslandResearchData {
  id: string;
  name: string;
  biome: string;
  type: string;
  radius: number;
  isPermanent: boolean;
  // Claim info
  isClaimed: boolean;
  ownerId: string | null;
  ownerName: string | null;
  ownerType: 'player' | 'crew' | 'faction_regime' | 'faction_navy' | 'faction_pirate' | 'faction_merchant' | 'faction_smuggler' | null;
  customName: string | null;
  claimedAt: string | null;
  regionDisplay?: string | null;
  // Flavor
  description: string;
  resources: string[];
  dangerLevel: number;
  region: string;
}

interface IslandResearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  islandId: string | null;
  islandPosition: { x: number; y: number } | null;
  playerPosition: { x: number; y: number } | null;
  refreshNonce?: number;
  onClaimTerritory?: (islandData: IslandResearchData) => void;
  onStartSiege?: (islandId: string, islandName: string) => void;
  onManageTerritory?: (islandId: string, islandName: string, isOwner: boolean) => void;
  onViewStatus?: (islandId: string, islandName: string) => void;
  playerId?: string;
  canDock?: boolean;
  isDocked?: boolean;
  isDockedHere?: boolean;
  onDock?: (islandId: string) => void;
  onUndock?: () => void;
}

// ============ CONSTANTS ============

const RESEARCH_DURATION_MS = 3500; // 3.5 seconds like EFT inspect

// Note: Biome descriptions and resources are generated server-side

// Inject shimmer animation styles
const injectStyles = (): void => {
  const styleId = 'island-research-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes researchProgress {
      0% { width: 0%; }
      100% { width: 100%; }
    }
    @keyframes researchPulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    @keyframes researchScan {
      0% { transform: translateY(0); opacity: 0.8; }
      100% { transform: translateY(100%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
};

// ============ COMPONENT ============

export const IslandResearchPanel: React.FC<IslandResearchPanelProps> = ({
  isOpen,
  onClose,
  islandId,
  islandPosition,
  playerPosition,
  refreshNonce,
  onClaimTerritory,
  onStartSiege,
  onManageTerritory,
  onViewStatus,
  playerId,
  canDock,
  isDocked,
  isDockedHere,
  onDock,
  onUndock,
}) => {
  const [isResearching, setIsResearching] = useState<boolean>(false);
  const [researchProgress, setResearchProgress] = useState<number>(0);
  const [researchData, setResearchData] = useState<IslandResearchData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Reset state when panel opens/closes or island changes
  // Check for cached research data first, then check ownership
  useEffect(() => {
    if (isOpen && islandId) {
      setIsResearching(false);
      setResearchProgress(0);
      setError(null);
      
      // Check if we already have this island researched in localStorage
      const cachedData = getResearchedIsland(islandId);
      if (cachedData) {
        setResearchData(cachedData);
        setResearchProgress(100);

        // Still refresh from server in the background so ownership/name stays current
        const refreshFromServer = async (): Promise<void> => {
          try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/world/island/${islandId}`);
            if (response.ok) {
              const data = await response.json();
              setResearchData(data);
              saveResearchedIsland(data);
            }
          } catch {
            // ignore
          }
        };

        refreshFromServer();
        return;
      }
      
      setResearchData(null);
      
      // Quick check if player might own this island - fetch data immediately
      // The server will return ownerId which we can compare to playerId
      // For owned territories, we skip the research timer
      const checkOwnership = async (): Promise<void> => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${apiUrl}/api/world/island/${islandId}`);
          if (response.ok) {
            const data = await response.json();
            // If player owns this island, auto-complete research and save
            if (data.ownerId && data.ownerId === playerId) {
              setResearchData(data);
              setResearchProgress(100);
              saveResearchedIsland(data);
            }
          }
        } catch {
          // Silently fail - player will need to research manually
        }
      };
      
      if (playerId) {
        checkOwnership();
      }
    }
  }, [isOpen, islandId, playerId, refreshNonce]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch island data from server
  const fetchIslandData = useCallback(async (): Promise<void> => {
    if (!islandId) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/world/island/${islandId}`);
      
      if (!response.ok) {
        throw new Error('Failed to research island');
      }
      
      const data = await response.json();
      setResearchData(data);
      // Save to localStorage for persistence
      saveResearchedIsland(data);
    } catch (err) {
      setError('Research failed. The island remains mysterious...');
      console.error('Island research error:', err);
    }
  }, [islandId]);

  // Start research process
  const startResearch = useCallback((): void => {
    if (isResearching || researchData) return;
    
    // Play research start SFX
    const soundManager = getSoundManager();
    soundManager?.play(SFX_KEYS.RESEARCH_START);
    
    setIsResearching(true);
    setResearchProgress(0);
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / RESEARCH_DURATION_MS) * 100);
      setResearchProgress(progress);
      
      if (progress >= 100) {
        clearInterval(interval);
        setIsResearching(false);
        // Play research complete SFX
        soundManager?.play(SFX_KEYS.RESEARCH_COMPLETE);
        fetchIslandData();
      }
    }, 50);
  }, [isResearching, researchData, fetchIslandData]);

  // Cancel research if panel closes during research
  useEffect(() => {
    if (!isOpen && isResearching) {
      setIsResearching(false);
      setResearchProgress(0);
    }
  }, [isOpen, isResearching]);

  if (!isOpen || !islandId) return null;

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDangerColor = (level: number): string => {
    if (level <= 2) return '#2ecc71';
    if (level <= 4) return '#f39c12';
    if (level <= 6) return '#e67e22';
    return '#e74c3c';
  };

  const getDangerLabel = (level: number): string => {
    if (level <= 2) return 'Safe';
    if (level <= 4) return 'Moderate';
    if (level <= 6) return 'Dangerous';
    return 'Deadly';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          minWidth: '320px',
          maxWidth: '400px',
          fontFamily: '"Sometype Mono", monospace',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold' }}>
            ISLAND RESEARCH
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {/* Not yet researched - two column layout */}
          {!researchData && !isResearching && !error && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Left column - icon */}
              <div style={{
                width: '64px',
                height: '64px',
                flexShrink: 0,
                border: '2px dashed #58a6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ color: '#58a6ff', fontSize: '28px', fontWeight: 'bold' }}>?</span>
              </div>
              {/* Right column - description and button */}
              <div style={{ flex: 1 }}>
                <p style={{ color: '#8b949e', fontSize: '12px', margin: '0 0 12px 0' }}>
                  This island has not been researched yet.
                </p>
                <button
                  onClick={startResearch}
                  style={{
                    backgroundColor: '#238636',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontFamily: '"Sometype Mono", monospace',
                    fontWeight: 'bold',
                  }}
                >
                  BEGIN RESEARCH
                </button>
              </div>
            </div>
          )}

          {/* Researching - two column layout */}
          {isResearching && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {/* Left column - animated icon */}
              <div style={{
                width: '64px',
                height: '64px',
                flexShrink: 0,
                border: '2px solid #58a6ff',
                animation: 'researchPulse 0.8s ease-in-out infinite',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: '#58a6ff',
                    animation: 'researchScan 1.2s ease-in-out infinite',
                  }}
                />
              </div>
              {/* Right column - progress */}
              <div style={{ flex: 1 }}>
                <p style={{ color: '#58a6ff', fontSize: '12px', margin: '0 0 8px 0' }}>
                  Researching island...
                </p>
                <div
                  style={{
                    backgroundColor: '#21262d',
                    height: '6px',
                    overflow: 'hidden',
                    marginBottom: '4px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#58a6ff',
                      height: '100%',
                      width: `${researchProgress}%`,
                      transition: 'width 0.05s linear',
                    }}
                  />
                </div>
                <p style={{ color: '#8b949e', fontSize: '10px', margin: 0 }}>
                  {Math.round(researchProgress)}% complete
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto',
                  border: '2px solid #f85149',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ color: '#f85149', fontSize: '24px', fontWeight: 'bold' }}>!</span>
                </div>
              </div>
              <p style={{ color: '#f85149', fontSize: '12px', marginBottom: '16px' }}>
                {error}
              </p>
              <button
                onClick={() => {
                  setError(null);
                  startResearch();
                }}
                style={{
                  backgroundColor: '#21262d',
                  color: '#c9d1d9',
                  border: '1px solid #30363d',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontFamily: '"Sometype Mono", monospace',
                }}
              >
                TRY AGAIN
              </button>
            </div>
          )}

          {/* Research complete - show data */}
          {researchData && (
            <div>
              {/* Island name and type */}
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <h3 style={{ color: '#ffd700', fontSize: '16px', margin: '0 0 4px 0' }}>
                  {researchData.customName || researchData.name || 'Unnamed Island'}
                </h3>
                <span
                  style={{
                    color: '#8b949e',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                  }}
                >
                  {researchData.type} • {researchData.biome}
                </span>
                <div
                  style={{
                    marginTop: '4px',
                    fontSize: '9px',
                    color: '#6e7681',
                  }}
                >
                  catalog reference: <span style={{ color: '#c9d1d9' }}>{researchData.id}</span>
                </div>
              </div>

              {/* Stats row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    backgroundColor: '#161b22',
                    padding: '8px',
                    border: '1px solid #30363d',
                  }}
                >
                  <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '2px' }}>
                    REGION
                  </div>
                  <div style={{ color: '#c9d1d9', fontSize: '11px' }}>
                    {researchData.regionDisplay || researchData.region || 'Unknown'}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: '#161b22',
                    padding: '8px',
                    border: '1px solid #30363d',
                  }}
                >
                  <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '2px' }}>
                    DANGER LEVEL
                  </div>
                  <div style={{ color: getDangerColor(researchData.dangerLevel), fontSize: '11px' }}>
                    {getDangerLabel(researchData.dangerLevel)} ({researchData.dangerLevel}/10)
                  </div>
                </div>
              </div>

              {/* Description */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  padding: '10px',
                  border: '1px solid #30363d',
                  marginBottom: '12px',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '4px' }}>
                  OBSERVATIONS
                </div>
                <p style={{ color: '#c9d1d9', fontSize: '11px', margin: 0, lineHeight: '1.5' }}>
                  {researchData.description}
                </p>
              </div>

              {/* Resources */}
              {researchData.resources.length > 0 && (
                <div
                  style={{
                    backgroundColor: '#161b22',
                    padding: '10px',
                    border: '1px solid #30363d',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '6px' }}>
                    POTENTIAL RESOURCES
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {researchData.resources.map((resource, i) => (
                      <span
                        key={i}
                        style={{
                          backgroundColor: '#21262d',
                          color: '#7ee787',
                          fontSize: '10px',
                          padding: '2px 6px',
                          border: '1px solid #238636',
                        }}
                      >
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Docking Actions */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  padding: '10px',
                  border: `1px solid ${isDockedHere ? '#3fb950' : '#30363d'}`,
                  marginBottom: '12px',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '6px' }}>
                  DOCKING
                </div>
                {isDockedHere ? (
                  <div>
                    <div style={{ color: '#3fb950', fontSize: '11px', marginBottom: '8px' }}>
                      [DOCKED] - Your ship is safely anchored here
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                      While docked, you cannot engage or be engaged in combat.
                    </div>
                    {onUndock && (
                      <button
                        onClick={onUndock}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#1f6feb',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        SET SAIL
                      </button>
                    )}
                  </div>
                ) : isDocked ? (
                  <div>
                    <div style={{ color: '#f0883e', fontSize: '11px', marginBottom: '8px' }}>
                      [DOCKED ELSEWHERE] - Undock first to dock here
                    </div>
                    {onUndock && (
                      <button
                        onClick={onUndock}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#1f6feb',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        FORCE UNDOCK
                      </button>
                    )}
                  </div>
                ) : canDock ? (
                  <div>
                    <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
                      [IN RANGE] - You can dock at this island
                    </div>
                    {onDock && islandId && (
                      <button
                        onClick={() => onDock(islandId)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#238636',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        DROP ANCHOR
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#8b949e', fontSize: '11px' }}>
                    [OUT OF RANGE] - Move closer to dock (within 3T)
                  </div>
                )}
              </div>

              {/* Claim info */}
              <div
                style={{
                  backgroundColor: researchData.isClaimed ? '#1c2128' : '#161b22',
                  padding: '10px',
                  border: `1px solid ${researchData.isClaimed ? '#3fb950' : '#30363d'}`,
                  position: 'relative',
                }}
              >
                {researchData.isClaimed && researchData.ownerType === 'faction_regime' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: 8,
                      background: '#e74c3c',
                      color: '#0d1117',
                      padding: '2px 8px',
                      fontSize: '10px',
                      fontWeight: 700,
                      border: '1px solid #ff7b72',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    Regime
                  </div>
                )}
                <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '6px' }}>
                  TERRITORY STATUS
                </div>
                {researchData.isClaimed ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ color: '#3fb950', fontSize: '12px', fontWeight: 'bold' }}>[CLAIMED]</span>
                      <span style={{ color: '#c9d1d9', fontSize: '11px' }}>
                        by{' '}
                        <strong
                          style={{
                            color: researchData.ownerType === 'faction_regime' ? '#f85149' : '#58a6ff',
                            textTransform: researchData.ownerType === 'faction_regime' ? 'uppercase' : 'none',
                          }}
                        >
                          {(researchData.ownerName && researchData.ownerName.trim().length > 0)
                            ? researchData.ownerName
                            : researchData.ownerType === 'faction_regime'
                              ? 'Regime'
                              : 'Unknown'}
                        </strong>
                      </span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                      {researchData.ownerType === 'faction_regime' 
                        ? 'Regime Territory' 
                        : researchData.ownerType === 'faction_navy'
                          ? 'Navy Territory'
                          : researchData.ownerType === 'faction_pirate'
                            ? 'Pirate Territory'
                            : researchData.ownerType === 'faction_merchant'
                              ? 'Merchant Guild Territory'
                              : researchData.ownerType === 'faction_smuggler'
                                ? 'Smuggler Territory'
                                : researchData.ownerType === 'crew' 
                                  ? 'Fleet Territory' 
                                  : 'Player Territory'}
                      {researchData.claimedAt && (
                        <> | Since {formatDate(researchData.claimedAt)}</>
                      )}
                    </div>
                    {/* Manage Territory button - for owners and visitors */}
                    {onManageTerritory && (
                      <button
                        onClick={() => onManageTerritory(
                          researchData.id,
                          researchData.customName || researchData.name,
                          researchData.ownerId === playerId
                        )}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#58a6ff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                          marginBottom: '8px',
                        }}
                      >
                        {researchData.ownerId === playerId ? 'MANAGE TERRITORY' : 'VIEW TERRITORY'}
                      </button>
                    )}
                    {/* View Status button - for owners to see garrison and permissions */}
                    {onViewStatus && researchData.ownerId === playerId && (
                      <button
                        onClick={() => onViewStatus(researchData.id, researchData.customName || researchData.name)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#238636',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                          marginBottom: '8px',
                        }}
                      >
                        VIEW STATUS
                      </button>
                    )}
                    {/* Siege button - only show if not owner */}
                    {onStartSiege && playerId && researchData.ownerId !== playerId && researchData.ownerType !== 'faction_regime' && researchData.ownerId !== 'regime' && (
                      <button
                        onClick={() => onStartSiege(researchData.id, researchData.customName || researchData.name)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#f85149',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        CONTEST TERRITORY
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
                      [UNCLAIMED] - Available for territorial claim
                    </div>
                    {onClaimTerritory && !researchData.isPermanent && (
                      <button
                        onClick={() => onClaimTerritory(researchData)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#238636',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        CLAIM TERRITORY
                      </button>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};
